import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  AppBar,
  Typography,
  Toolbar,
  Button,
  styled,
  Grid,
  Container,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useAuth, useBrowserQuery } from '@/hooks/common'
import { useEffect, useState } from 'react'
import { AxiosProvider } from '@/context/Axios'
import GuestLandingHeader from './GuestLandingHeader'
import { IMAGES } from '@/assets/images'
import { SVGS } from '@/assets/svgs'
import SVG from 'react-inlinesvg'
import { GuestTabs, HOME_PATH, TopicType } from '@/utils/constants'
import './styles.css'
import colors from '@/styles/colors'
import PatternItemCard from './PatternItemCard'
import Footer from '@/components/Footer'
import { RoundedFilledButton } from '@/components/StyledComponents'
import AuthDialog from '../auth/AuthDialog'
import useAppStore, { clearAllStores } from '@/store/useAppStore'
import AnimatedEditorShowcaseFixed from './AnimatedEditorShowcaseFixed'
import AnimatedWordRotator from './AnimatedWordRotator'
import GuestPatternsFeed from './GuestPatternsFeed'

const useStyles = () => ({
  square: {
    position: 'relative',
    '&::before': {
      display: 'block',
      content: "''",
      paddingBottom: '100%',
    },
  },
})

const PlainButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: colors.themeBlack,
  fontFamily: 'Inter',
  fontSize: 14,
  fontWeight: '600',
  borderRadius: 50,

  alignItems: 'center',
  padding: '10px 40px',
  '&:hover': {
    backgroundColor: theme.palette.brand.light,
    color: 'white',
    fontSize: 14,
    fontWeight: '700',
  },
}))

export default function GuestLandingLayout() {
  //const classes = useStyles()
  const { user } = useAuth()
  const navigate = useNavigate()
  const query = useBrowserQuery()
  const location = useLocation()
  const authStep = query.get('step') || ''
  const showAuthDialog = location.pathname == '/auth' && authStep.length > 0
  const theme = useTheme()
  const lessThanSmall = useMediaQuery(theme.breakpoints.down('md'))
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0)

  useEffect(() => {
    if (user?.userID) {
      // Check for redirect parameter
      const redirectPath = query.get('redirect')
      if (redirectPath) {
        navigate(redirectPath)
      } else {
        navigate(HOME_PATH)
      }
    }
  }, [user?.userID])

  const onClickLogin = () => {
    navigate('/login')
  }

  return (
    <AxiosProvider>
      <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
        <AppBar elevation={0} position='fixed' sx={{ width: '100%', opacity: '1 !important' }}>
          <GuestLandingHeader
            selectedTabIndex={selectedTabIndex}
            setSelectedTabIndex={setSelectedTabIndex}
          />
        </AppBar>
        <Toolbar />
        <Box component='main' sx={{ width: '100%', bgcolor: 'background.default' }}>
          <Box
            display='flex'
            flexDirection='row'
            width={'100%'}
            sx={{
              transition: 'margin 0.3s',
              padding: { xs: '60px 24px', md: '80px 40px' },
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(180deg, #fafbff 0%, #ffffff 100%)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                maxWidth: '1400px',
                width: '100%',
                gap: 6,
              }}
            >
              <Box sx={{ display: 'flex', flex: 0.9, flexDirection: 'column', width: '100%' }}>
                <Typography
                  sx={{
                    fontSize: { xs: '42px', md: '56px', lg: '68px' },
                    fontWeight: 800,
                    lineHeight: 1.1,
                    color: '#0f172a',
                    letterSpacing: '-0.03em',
                    mb: 3,
                  }}
                >
                  Create Stunning{' '}
                  <AnimatedWordRotator
                    words={['Patterns', 'Quilts', 'Collections', 'Galleries', 'Designs']}
                    interval={2500}
                    fontSize={{ xs: '42px', md: '56px', lg: '68px' }}
                    fontWeight={800}
                    letterSpacing='-0.03em'
                  />
                  <br />
                  in Minutes
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '22px', md: '28px' },
                    fontWeight: 600,
                    lineHeight: 1.3,
                    color: '#6366f1',
                    letterSpacing: '-0.02em',
                  }}
                >
                  Trusted by 10,000+ creators worldwide
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '17px', md: '19px' },
                    fontWeight: 400,
                    lineHeight: 1.7,
                    color: '#475569',
                    mt: 4,
                    maxWidth: '620px',
                  }}
                >
                  Create beautiful patterns, quilts, collections, and galleries with our intuitive
                  interactive editor. See changes instantly, share with your community, and export
                  print-ready files—all in your browser.
                </Typography>
                {lessThanSmall && (
                  <Box
                    display='flex'
                    sx={{ alignItems: 'center', justifyContent: 'center', my: 4 }}
                  >
                    <SVG src={SVGS.Quilt} color='white' width={'100%'} height={'100%'} />
                  </Box>
                )}
                <Box
                  sx={{ mt: 5, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}
                >
                  <Button
                    variant='contained'
                    sx={{
                      backgroundColor: '#6366f1',
                      color: 'white',
                      fontSize: '17px',
                      fontWeight: 600,
                      padding: '16px 36px',
                      borderRadius: '12px',
                      textTransform: 'none',
                      boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      letterSpacing: '-0.01em',
                      '&:hover': {
                        backgroundColor: '#5b52d9',
                        boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)',
                        transform: 'translateY(-2px)',
                      },
                      '&:active': {
                        transform: 'translateY(0px)',
                      },
                    }}
                    onClick={() => navigate('/pattern')}
                  >
                    Try it Free — No Sign Up
                  </Button>
                  <Button
                    variant='outlined'
                    sx={{
                      borderWidth: '2px',
                      borderColor: '#e2e8f0',
                      color: '#0f172a',
                      fontSize: '17px',
                      fontWeight: 600,
                      padding: '14px 36px',
                      borderRadius: '12px',
                      textTransform: 'none',
                      boxShadow: 'none',
                      letterSpacing: '-0.01em',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        borderWidth: '2px',
                        borderColor: '#6366f1',
                        backgroundColor: 'rgba(99, 102, 241, 0.04)',
                        color: '#6366f1',
                        boxShadow: 'none',
                        transform: 'translateY(-2px)',
                      },
                      '&:active': {
                        transform: 'translateY(0px)',
                      },
                    }}
                    onClick={() => navigate('/signup')}
                  >
                    Create Account
                  </Button>
                </Box>
                {/* Trust Badge */}
                <Box
                  sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}
                >
                  <Typography
                    sx={{
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box component='span' sx={{ color: '#10b981', fontSize: '18px' }}>
                      ✓
                    </Box>
                    No credit card required
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box component='span' sx={{ color: '#10b981', fontSize: '18px' }}>
                      ✓
                    </Box>
                    Free forever
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box component='span' sx={{ color: '#f59e0b', fontSize: '18px' }}>
                      ⭐
                    </Box>
                    4.9/5 from 2,000+ reviews
                  </Typography>
                </Box>
              </Box>
              {!lessThanSmall && (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <AnimatedEditorShowcaseFixed />
                </Box>
              )}
            </Box>
          </Box>
          <Box
            display='flex'
            flexDirection={lessThanSmall ? 'column' : 'row'}
            width={'100%'}
            sx={{
              padding: { xs: '56px 24px', md: '80px 40px' },
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              borderTop: '1px solid rgba(255, 255, 255, 0.08)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: lessThanSmall ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: 'center',
                maxWidth: '1200px',
                width: '100%',
                gap: lessThanSmall ? 4 : 6,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  flex: 1,
                  gap: 2.5,
                  padding: { xs: '24px 16px', md: '32px 24px' },
                  borderRadius: '16px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                    transform: 'translateY(-4px)',
                    '& .feature-icon': {
                      transform: 'scale(1.1)',
                      boxShadow: '0 8px 24px rgba(99, 102, 241, 0.35)',
                    },
                  },
                }}
              >
                <Box
                  className='feature-icon'
                  sx={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '16px',
                    background:
                      'linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(139, 92, 246, 0.25) 100%)',
                    border: '1px solid rgba(99, 102, 241, 0.35)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
                  }}
                >
                  <SVG src={SVGS.DesignPatterns} color='#a5b4fc' width={32} height={32} />
                </Box>
                <Typography
                  sx={{
                    fontSize: '20px',
                    fontWeight: 700,
                    color: 'white',
                    lineHeight: 1.3,
                    maxWidth: '280px',
                    letterSpacing: '-0.01em',
                  }}
                >
                  Real-Time Preview
                </Typography>
                <Typography
                  sx={{
                    fontSize: '15px',
                    fontWeight: 400,
                    color: '#cbd5e1',
                    lineHeight: 1.7,
                    maxWidth: '280px',
                  }}
                >
                  Watch your design come to life instantly. No waiting, no rendering.
                </Typography>
              </Box>

              <Box
                sx={{
                  width: lessThanSmall ? '60%' : '1px',
                  height: lessThanSmall ? '1px' : '120px',
                  background:
                    'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0) 100%)',
                  display: lessThanSmall ? 'none' : 'block',
                }}
              />

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  flex: 1,
                  gap: 2.5,
                  padding: { xs: '24px 16px', md: '32px 24px' },
                  borderRadius: '16px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                    transform: 'translateY(-4px)',
                    '& .feature-icon': {
                      transform: 'scale(1.1)',
                      boxShadow: '0 8px 24px rgba(139, 92, 246, 0.35)',
                    },
                  },
                }}
              >
                <Box
                  className='feature-icon'
                  sx={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '16px',
                    background:
                      'linear-gradient(135deg, rgba(139, 92, 246, 0.25) 0%, rgba(168, 85, 247, 0.25) 100%)',
                    border: '1px solid rgba(139, 92, 246, 0.35)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)',
                  }}
                >
                  <SVG src={SVGS.DesignMeasure} color='#c4b5fd' width={32} height={32} />
                </Box>
                <Typography
                  sx={{
                    fontSize: '20px',
                    fontWeight: 700,
                    color: 'white',
                    lineHeight: 1.3,
                    maxWidth: '280px',
                    letterSpacing: '-0.01em',
                  }}
                >
                  Pixel-Perfect Output
                </Typography>
                <Typography
                  sx={{
                    fontSize: '15px',
                    fontWeight: 400,
                    color: '#cbd5e1',
                    lineHeight: 1.7,
                    maxWidth: '280px',
                  }}
                >
                  Professional-quality results every time. Export print-ready files in seconds.
                </Typography>
              </Box>

              <Box
                sx={{
                  width: lessThanSmall ? '60%' : '1px',
                  height: lessThanSmall ? '1px' : '120px',
                  background:
                    'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0) 100%)',
                  display: lessThanSmall ? 'none' : 'block',
                }}
              />

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  flex: 1,
                  gap: 2.5,
                  padding: { xs: '24px 16px', md: '32px 24px' },
                  borderRadius: '16px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                    transform: 'translateY(-4px)',
                    '& .feature-icon': {
                      transform: 'scale(1.1)',
                      boxShadow: '0 8px 24px rgba(16, 185, 129, 0.35)',
                    },
                  },
                }}
              >
                <Box
                  className='feature-icon'
                  sx={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '16px',
                    background:
                      'linear-gradient(135deg, rgba(16, 185, 129, 0.25) 0%, rgba(5, 150, 105, 0.25) 100%)',
                    border: '1px solid rgba(16, 185, 129, 0.35)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
                  }}
                >
                  <SVG src={SVGS.DesignMultiple} color='#6ee7b7' width={32} height={32} />
                </Box>
                <Typography
                  sx={{
                    fontSize: '20px',
                    fontWeight: 700,
                    color: 'white',
                    lineHeight: 1.3,
                    maxWidth: '280px',
                    letterSpacing: '-0.01em',
                  }}
                >
                  Build Together
                </Typography>
                <Typography
                  sx={{
                    fontSize: '15px',
                    fontWeight: 400,
                    color: '#cbd5e1',
                    lineHeight: 1.7,
                    maxWidth: '280px',
                  }}
                >
                  Join 10,000+ designers sharing ideas. Get inspired and grow faster.
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              width: '100%',
              padding: { xs: '60px 24px', md: '80px 40px' },
              backgroundColor: 'white',
            }}
          >
            <Box
              sx={{
                maxWidth: '1200px',
                margin: '0 auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '36px', md: '48px' },
                  fontWeight: 800,
                  lineHeight: 1.2,
                  color: '#0f172a',
                  letterSpacing: '-0.02em',
                  textAlign: 'center',
                  mb: 3,
                }}
              >
                Create Beautiful Designs in 3 Easy Steps
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '17px', md: '20px' },
                  fontWeight: 400,
                  lineHeight: 1.6,
                  color: '#64748b',
                  textAlign: 'center',
                  mb: 8,
                  maxWidth: '640px',
                }}
              >
                No experience required. Start creating in under 60 seconds.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 4, md: 6 },
                  width: '100%',
                  alignItems: { xs: 'center', md: 'flex-start' },
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    maxWidth: { xs: '100%', md: '300px' },
                  }}
                >
                  <Box
                    sx={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      boxShadow: '0 4px 20px rgba(99, 102, 241, 0.3)',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '32px',
                        fontWeight: 700,
                        color: 'white',
                      }}
                    >
                      1
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '24px',
                      fontWeight: 700,
                      color: '#0f172a',
                      mb: 2,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    Pick Your Starting Point
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 400,
                      color: '#64748b',
                      lineHeight: 1.7,
                    }}
                  >
                    Choose from 500+ templates or start from scratch
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    pt: 5,
                  }}
                >
                  <Box
                    sx={{
                      width: '60px',
                      height: '2px',
                      background: 'linear-gradient(90deg, #e2e8f0 0%, #cbd5e1 50%, #e2e8f0 100%)',
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    maxWidth: { xs: '100%', md: '300px' },
                  }}
                >
                  <Box
                    sx={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      boxShadow: '0 4px 20px rgba(139, 92, 246, 0.3)',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '32px',
                        fontWeight: 700,
                        color: 'white',
                      }}
                    >
                      2
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '24px',
                      fontWeight: 700,
                      color: '#0f172a',
                      mb: 2,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    Edit & Customize
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 400,
                      color: '#64748b',
                      lineHeight: 1.7,
                    }}
                  >
                    Drag, drop, and adjust patterns, fabrics, and colors instantly
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    pt: 5,
                  }}
                >
                  <Box
                    sx={{
                      width: '60px',
                      height: '2px',
                      background: 'linear-gradient(90deg, #e2e8f0 0%, #cbd5e1 50%, #e2e8f0 100%)',
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    maxWidth: { xs: '100%', md: '300px' },
                  }}
                >
                  <Box
                    sx={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      boxShadow: '0 4px 20px rgba(16, 185, 129, 0.3)',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '32px',
                        fontWeight: 700,
                        color: 'white',
                      }}
                    >
                      3
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '24px',
                      fontWeight: 700,
                      color: '#0f172a',
                      mb: 2,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    Download & Share
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 400,
                      color: '#64748b',
                      lineHeight: 1.7,
                    }}
                  >
                    Export print-ready PDFs, share with the community, or order fabric directly
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            display='flex'
            flexDirection='row'
            sx={{
              transition: 'margin 0.3s',
              alignItems: 'center',
              justifyContent: 'center',
              padding: { xs: '60px 24px', md: '80px 40px' },
              maxWidth: '1200px',
              margin: '0 auto',
              gap: { xs: 4, md: 8 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                maxWidth: { xs: '100%', md: '50%' },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '32px', md: '40px', lg: '48px' },
                  fontWeight: 800,
                  lineHeight: 1.2,
                  color: '#0f172a',
                  letterSpacing: '-0.02em',
                  mb: 4,
                }}
              >
                Why 10,000+ Designers Choose Meshdom
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '16px', md: '18px' },
                  fontWeight: 400,
                  lineHeight: 1.7,
                  color: '#64748b',
                  mb: 5,
                }}
              >
                Traditional design software is expensive and complicated. Meshdom works in your
                browser—no installation, no learning curve. Design on any device. Your work syncs
                everywhere automatically.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2.5,
                  mb: 4,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(99, 102, 241, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#6366f1' }}>
                      ✓
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{ fontSize: '16px', fontWeight: 600, color: '#1a1a1a', mb: 0.5 }}
                    >
                      Real-Time Visual Preview
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 400,
                        color: '#64748b',
                        lineHeight: 1.6,
                      }}
                    >
                      See your design come to life as you work. Instant feedback for every change.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(139, 92, 246, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#8b5cf6' }}>
                      ✓
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{ fontSize: '16px', fontWeight: 600, color: '#1a1a1a', mb: 0.5 }}
                    >
                      500+ Professional Templates
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 400,
                        color: '#64748b',
                        lineHeight: 1.6,
                      }}
                    >
                      Start with proven designs. Customize colors, fabrics, and layouts instantly.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#10b981' }}>
                      ✓
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{ fontSize: '16px', fontWeight: 600, color: '#1a1a1a', mb: 0.5 }}
                    >
                      Share & Sell Your Work
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 400,
                        color: '#64748b',
                        lineHeight: 1.6,
                      }}
                    >
                      Build your portfolio and monetize your designs through our marketplace.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {lessThanSmall && (
                <Box
                  sx={{
                    width: '100%',
                    mb: 4,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <img
                    src={IMAGES.guestPatterns}
                    width={'100%'}
                    height={'100%'}
                    style={{ display: 'block' }}
                  />
                </Box>
              )}

              <Box>
                <Button
                  onClick={onClickLogin}
                  variant='contained'
                  sx={{
                    backgroundColor: '#6366f1',
                    color: 'white',
                    fontSize: '17px',
                    fontWeight: 600,
                    padding: '16px 36px',
                    borderRadius: '12px',
                    textTransform: 'none',
                    letterSpacing: '-0.01em',
                    boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: '#5b52d9',
                      boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)',
                      transform: 'translateY(-2px)',
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                    },
                  }}
                >
                  Start Creating Now
                </Button>
              </Box>
            </Box>

            {!lessThanSmall && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  maxWidth: { xs: '100%', md: '50%' },
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <img
                    src={IMAGES.guestPatterns}
                    width={'100%'}
                    height={'100%'}
                    style={{ display: 'block' }}
                  />
                </Box>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              width: '100%',
              // padding: { xs: '60px 24px', md: '80px 40px' },
              backgroundColor: '#fafbff',
            }}
          >
            <Box
              sx={{
                margin: '0 auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '36px', md: '48px' },
                  fontWeight: 800,
                  lineHeight: 1.2,
                  color: '#0f172a',
                  letterSpacing: '-0.02em',
                  textAlign: 'center',
                  mb: 3,
                }}
              >
                Trending Community Designs
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '17px', md: '20px' },
                  fontWeight: 400,
                  lineHeight: 1.6,
                  color: '#64748b',
                  textAlign: 'center',
                  mb: 5,
                }}
              >
                Join thousands sharing their work. Get inspired and build your portfolio.
              </Typography>

              <GuestPatternsFeed />

              <Box
                sx={{
                  mt: 6,
                  mb: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 3,
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '16px',
                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <SVG src={SVGS.ThumbIcon} color='#6366f1' width={32} height={32} />
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '18px', md: '22px' },
                    fontWeight: 600,
                    lineHeight: 1.5,
                    color: '#1a1a1a',
                    maxWidth: '600px',
                  }}
                >
                  Join thousands of creators bringing their ideas to life with Meshdom
                </Typography>
                <Button
                  variant='contained'
                  sx={{
                    backgroundColor: '#6366f1',
                    color: 'white',
                    fontSize: '17px',
                    fontWeight: 600,
                    padding: '16px 36px',
                    borderRadius: '12px',
                    textTransform: 'none',
                    letterSpacing: '-0.01em',
                    boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: '#5b52d9',
                      boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)',
                      transform: 'translateY(-2px)',
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                    },
                  }}
                  onClick={onClickLogin}
                >
                  Start Your First Design Now
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
        <Footer />
        <AuthDialog authStep={authStep} open={showAuthDialog} onClose={() => navigate('/')} />
      </Box>
    </AxiosProvider>
  )
}
